import type { Tour } from '../../types'
import { target } from './target'
import { canSearchClinical } from '@/shared/rbac'
import { navGroupBody } from './nav-items'

/** Shell elements render with the app chrome — no need for a long wait. */
const SHELL_WAIT = 1500

export const appOverviewTour: Tour = {
  id: 'app-overview',
  version: 7,
  title: 'Application overview',
  kind: 'overview',
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to MediCodio',
      body: 'This quick tour shows where things live. It only covers the areas your role can open, and you can leave at any time with Esc.',
    },
    // The sidebar logo links to /workspace/queue (sidebar.tsx), so the logo hint is only
    // true for users whose home is the Chart Queue.
    {
      id: 'home',
      // Only offered when the home route's own guard passes (see canNavigateTo).
      route: (a) => a.homeRoute,
      access: { when: (a) => a.homeRoute === '/workspace/queue' },
      target: target('shell.main'),
      side: 'left',
      title: 'Your home page',
      body: 'This is where you land after signing in. You can always come back here by clicking the MediCodio logo at the top of the sidebar.',
    },
    {
      id: 'home-other',
      route: (a) => a.homeRoute,
      access: { when: (a) => a.homeRoute !== '/workspace/queue' },
      target: target('shell.main'),
      side: 'left',
      title: 'Your home page',
      body: 'This is where you land after signing in.',
    },
    {
      id: 'sidebar',
      target: target('shell.sidebar'),
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Main navigation',
      body: 'Everything you can open is in this sidebar, grouped by area. You only see the items your role gives you, so it may look different for your colleagues.',
      fallbackBody: 'The sidebar is hidden at this screen size; widen the window to see it. It lists everything you can open, grouped by area, and only shows the items your role gives you.',
    },
    {
      id: 'g-workspace',
      target: target('navgroup:workspace'),
      access: { navGroup: 'Workspace' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Workspace',
      body: 'Your daily coding work.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Workspace', 'Your daily coding work.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Workspace', 'Your daily coding work.')}`,
    },
    {
      id: 'g-dashboards',
      target: target('navgroup:dashboards'),
      access: { navGroup: 'Dashboards' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Dashboards',
      body: 'Dashboards for the work you can see.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Dashboards', 'Dashboards for the work you can see.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Dashboards', 'Dashboards for the work you can see.')}`,
    },
    {
      id: 'g-library',
      target: target('navgroup:master-library'),
      access: { navGroup: 'Master Library' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Master Library',
      body: 'Reference data you can open.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Master Library', 'Reference data you can open.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Master Library', 'Reference data you can open.')}`,
    },
    {
      id: 'g-clients',
      target: target('navgroup:clients'),
      access: { navGroup: 'Clients' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Clients',
      body: 'Your clients.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Clients', 'Your clients.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Clients', 'Your clients.')}`,
    },
    {
      id: 'g-my-time',
      target: target('navgroup:my-time'),
      access: { navGroup: 'My Time' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'My Time',
      body: 'Your own time.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'My Time', 'Your own time.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'My Time', 'Your own time.')}`,
    },
    {
      id: 'g-finance',
      target: target('navgroup:finance-overview'),
      access: { navGroup: 'Finance Overview' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Finance Overview',
      body: 'Billing for the clients you can see.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Finance Overview', 'Billing for the clients you can see.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Finance Overview', 'Billing for the clients you can see.')}`,
    },
    {
      id: 'g-access',
      target: target('navgroup:access'),
      access: { navGroup: 'Access' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'Access Requests',
      body: 'Access to more of the platform.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'Access', 'Access to more of the platform.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'Access', 'Access to more of the platform.')}`,
    },
    {
      id: 'g-system',
      target: target('navgroup:system'),
      // Same rule as the sidebar's System Admin item (system_admin read). For
      // everyone else the group holds only Announcements, which the header
      // "announcements" step below already covers (no back-to-back duplicate).
      // The System Admin ROUTE needs system_admin read:all (canAdminSystem), stricter than the sidebar.
      access: { navGroup: 'System', gate: 'canAdminSystem' },
      side: 'right',
      waitMs: SHELL_WAIT,
      title: 'System',
      body: 'Platform administration and updates.',
      // Describe ONLY the items this user's sidebar shows (same predicate as the sidebar).
      bodyFrom: (ctx) => navGroupBody(ctx, 'System', 'Platform administration and updates.'),
      fallbackFrom: (ctx) => `The sidebar is hidden at this screen size; widen the window to see it. ${navGroupBody(ctx, 'System', 'Platform administration and updates.')}`,
    },
    {
      id: 'search',
      target: target('header.search'),
      // Mirrors the header's own condition for rendering the search box.
      access: { when: canSearchClinical },
      side: 'bottom',
      waitMs: SHELL_WAIT,
      title: 'Search everything',
      body: 'Search codes from anywhere. Type here, or press Ctrl+K (Cmd+K on Mac) to jump here; results show as you type.',
      // Charts, providers and facilities only for users who can read charts.
      bodyFrom: (ctx) =>
        ctx.access.can('chart_queue', 'read')
          ? 'Search codes, charts, providers and facilities from anywhere. Type here, or press Ctrl+K (Cmd+K on Mac) to jump here; results show as you type. Paste a list of chart IDs here to find all of those charts at once.'
          : 'Search codes from anywhere. Type here, or press Ctrl+K (Cmd+K on Mac) to jump here; results show as you type.',
      fallbackFrom: (ctx) =>
        ctx.access.can('chart_queue', 'read')
          ? 'Press Ctrl+K (Cmd+K on Mac) to search. It finds codes, charts, providers and facilities from anywhere, and a pasted list of chart IDs finds all of those charts at once.'
          : 'Press Ctrl+K (Cmd+K on Mac) to search. It finds codes from anywhere.',
    },
    {
      id: 'announcements',
      target: target('header.announcements'),
      access: { gate: 'canViewAnnouncements' },
      side: 'bottom',
      waitMs: SHELL_WAIT,
      title: 'Announcements',
      body: 'Product, guideline and payer updates. A badge shows how many you haven\'t read yet.',
    },
    {
      id: 'notifications',
      target: target('header.notifications'),
      side: 'bottom',
      waitMs: SHELL_WAIT,
      title: 'Notifications',
      body: 'Mentions, assignments and system alerts arrive here. Open the bell and choose "View all notifications" for the full list.',
      fallbackBody: 'The bell is off-screen at this width; widen the window to reach it. Mentions, assignments and system alerts arrive there.',
    },
    {
      id: 'ask-ai',
      target: target('shell.ask-ai'),
      side: 'left',
      waitMs: SHELL_WAIT,
      title: 'Ask AI',
      body: 'Ask the MediCodio assistant about the page you\'re on. You can drag the button to any corner of the screen.',
      fallbackBody: 'The Ask AI button isn\'t visible right now. When it is, it opens the MediCodio assistant for the page you\'re on.',
    },
    {
      id: 'help',
      target: target('header.help'),
      side: 'bottom',
      waitMs: SHELL_WAIT,
      title: 'Help and tours',
      body: 'Replay this tour or get a guide for the page you\'re on any time from here.',
      fallbackBody: 'The ? button is off-screen at this width; widen the window to reach it. It replays this tour and offers a guide for the page you\'re on.',
    },
    {
      id: 'user-menu',
      target: target('header.user-menu'),
      side: 'bottom',
      waitMs: SHELL_WAIT,
      access: { userFeature: { key: 'support_codio', on: false } },
      title: 'Your account',
      body: 'Your profile, preferences and sign-out live here.',
      fallbackBody: 'Your account menu (your initials, top right) is off-screen at this width; widen the window to reach it. It holds your profile, preferences and sign-out.',
    },
    {
      id: 'user-menu-support',
      target: target('header.user-menu'),
      side: 'bottom',
      waitMs: SHELL_WAIT,
      // Support lives in this menu (header.tsx, gated on features.support_codio).
      access: { userFeature: { key: 'support_codio', on: true } },
      title: 'Your account',
      // Support = a one-time handoff into the Support Codio customer portal, opened
      // in a new tab (useOpenSupport → POST /support/handoff).
      body: 'Your profile and sign-out live here, and "Support" opens the Support Codio portal in a new tab, where you can raise a support request with the Codio team and follow it.',
      fallbackBody: 'Your account menu (your initials, top right) is off-screen at this width; widen the window to reach it. It holds your profile and sign-out, and "Support", which opens the Support Codio portal to raise a support request.',
    },
    {
      id: 'done',
      title: 'You\'re all set',
      body: 'Open any area from the sidebar, then choose "Guide me through this page" from the ? button for a closer look.',
      // On a phone the header overflows and the ? button is off-screen.
      bodyIfHidden: { selector: '[data-tour="header.help"]', body: 'Open any area you can reach. On a wider screen, the ? button in the header replays this tour and offers a guide for each page.' },
    },
  ],
}
